/**
 * OrderNotificationsPanel — the "Buyer notifications" strip of the order modal.
 *
 * Compact by design (owner feedback, review rva86r): a single collapsed line
 * summarises what went to the buyer ("Confirmation sent 8/26 · Invoice copy
 * sent 8/27") with the Send now / Resend buttons on the right; expanding it
 * reveals the full audit history from GET /portal/orders/{id}/notifications
 * (kind, recipients, trigger, status, skip/error reason).
 *
 * Both senders, one timeline (owner direction 2026-09-09, kanban 9479e4de):
 * the portal's own relays (order_notification_events) are interleaved with
 * what Adam's sales site mailed on its own (read from its orders@ copies),
 * each row badged with its source and the sales-site rows carrying an
 * evidence line naming the source email. The mailbox copy shows To/Cc only,
 * so a Bcc'd recipient is invisible — the panel says so.
 *
 * Confirmation drift (kanban b9051e14): when the confirmation the buyer holds
 * shows a Surcharge / Freight the order will no longer bill (exemption toggled,
 * explicit /SURCHG line, freight waived or keyed after the email went out),
 * an always-visible banner under the strip names each charge, what the email
 * said, what will bill and why, and offers "Send confirmation update" — the
 * sales site's own Confirmation Update template with the current figures, to
 * everyone who got the stale ones.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { orderNotificationService } from '../../services/orderNotifications';
import type {
  ConfirmationDrift,
  NotificationKind,
  OrderNotificationEvent,
  OrderNotificationsResponse,
  SendNowKind,
} from '../../types/orderNotifications';
import { trackEvent } from '../../services/portalTracking';
import styles from './OrderNotifications.module.css';
import { apiErrorMessage as errorMessage } from './orderContactsUtils';
import { emitOrderNotificationsChanged } from './orderNotificationsRefresh';
import {
  KIND_LABEL,
  SALES_SITE_KIND_LABEL,
  TRIGGER_LABEL,
  driftHeadline,
  driftLineText,
  formatWhen,
  hasSent,
  mergeTimeline,
  salesSiteEvidence,
  sendButtonLabel,
  summaryLine,
} from './orderNotificationsUtils';

export interface OrderNotificationsPanelProps {
  orderId: string;
  canManage: boolean;
  /** Bump to force a reload (e.g. after the contact list changes). */
  refreshKey?: string | number;
  /** Test/DI seam — defaults to the real service. */
  service?: Pick<
    typeof orderNotificationService,
    'getOrderNotifications' | 'sendNow' | 'sendConfirmationUpdate'
  >;
  /** Confirmation prompt for resends; defaults to window.confirm. */
  confirmFn?: (message: string) => boolean;
}

function statusClass(status: OrderNotificationEvent['status']): string {
  switch (status) {
    case 'sent':
      return styles.statusSent;
    case 'failed':
      return styles.statusFailed;
    case 'skipped':
      return styles.statusSkipped;
    default:
      return styles.statusPending;
  }
}

export const OrderNotificationsPanel: React.FC<OrderNotificationsPanelProps> = ({
  orderId,
  canManage,
  refreshKey,
  service = orderNotificationService,
  confirmFn = (message) => window.confirm(message),
}) => {
  const [data, setData] = useState<OrderNotificationsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState<NotificationKind | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await service.getOrderNotifications(orderId);
      setData(resp);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load notification history.'));
    }
  }, [orderId, service]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const send = useCallback(
    async (kind: SendNowKind, e?: React.MouseEvent) => {
      // Buttons live inside <summary>; keep a click from toggling the details.
      e?.preventDefault();
      e?.stopPropagation();
      if (!data) return;
      const resend = hasSent(data.events, kind);
      if (
        resend &&
        !confirmFn(
          `${KIND_LABEL[kind]} was already emailed to the buyer contact(s). Send it again?`,
        )
      ) {
        // The click itself is recorded by the data-action on the button; the
        // decision in the confirm prompt is its own row (audit trail).
        trackEvent('click', 'order_notification_resend_cancelled', { order_id: orderId, kind, outcome: 'none' });
        return;
      }
      if (resend) {
        trackEvent('click', 'order_notification_resend_confirmed', { order_id: orderId, kind, outcome: 'request' });
      }
      setSending(kind);
      setSendError(null);
      try {
        const resp = await service.sendNow(orderId, kind);
        if (resp.event.status === 'failed') {
          setSendError(resp.event.error_message || 'The email could not be sent.');
        }
        await load();
        // The Confirmation contacts row shows the per-contact status; tell it.
        emitOrderNotificationsChanged(orderId);
      } catch (err) {
        setSendError(errorMessage(err, 'The email could not be sent.'));
      } finally {
        setSending(null);
      }
    },
    [data, orderId, service, load, confirmFn],
  );

  const sendUpdate = useCallback(async () => {
    if (!data || !data.confirmation_drift) return;
    const d = data.confirmation_drift;
    const to = Array.from(
      new Set([
        ...data.contacts.filter((c) => c.confirmation !== false).map((c) => c.email.toLowerCase()),
        ...d.told_recipients.map((r) => r.toLowerCase()),
      ]),
    );
    // Customer-facing and one-shot: say who gets it and what it tells them
    // before anything goes out (owner ask on review sj2gne). The decision in
    // the prompt is its own audit row, like the resend prompt above.
    const ok = confirmFn(
      [
        `Send the Confirmation Update that corrects "${d.told_subject}"?`,
        '',
        `To: ${to.join(', ')}`,
        '',
        'It tells them:',
        ...d.lines.map((line) => `• ${driftLineText(line)}`),
        '',
        'This goes out once: the portal refuses to send the same correction twice.',
      ].join('\n'),
    );
    if (!ok) {
      trackEvent('click', 'order_confirmation_update_cancelled', { order_id: orderId, outcome: 'none' });
      return;
    }
    trackEvent('click', 'order_confirmation_update_confirmed', { order_id: orderId, outcome: 'request' });
    setSending('confirmation_update');
    setSendError(null);
    try {
      const resp = await service.sendConfirmationUpdate(orderId);
      if (resp.event.status === 'failed') {
        setSendError(resp.event.error_message || 'The confirmation update could not be sent.');
      }
      await load();
      emitOrderNotificationsChanged(orderId);
    } catch (err) {
      setSendError(errorMessage(err, 'The confirmation update could not be sent.'));
    } finally {
      setSending(null);
    }
  }, [data, orderId, service, load, confirmFn]);

  const events = data?.events ?? [];
  const salesSiteSends = data?.sales_site_sends ?? [];
  const drift: ConfirmationDrift | null = data?.confirmation_drift ?? null;
  // Who the update goes to: the confirmation contacts plus everyone the stale
  // email reached (the sales site's own list) — shown so the CSR knows before
  // clicking (it is a customer-facing send).
  const updateRecipients = drift
    ? Array.from(
        new Set([
          ...(data?.contacts ?? [])
            .filter((c) => c.confirmation !== false)
            .map((c) => c.email.toLowerCase()),
          ...drift.told_recipients.map((r) => r.toLowerCase()),
        ]),
      )
    : [];
  const timeline = mergeTimeline(events, salesSiteSends);
  const contactsCount = data?.contacts.length ?? 0;
  // Why a Send-now button is off, shown as text (not only a hover title) so a
  // CSR sees the reason without guessing (kanban 7479fb71).
  const blockedReasons: { kind: NotificationKind; reason: string }[] = [];
  if (data && !data.can_send_confirmation && data.confirmation_blocked_reason) {
    blockedReasons.push({ kind: 'confirmation', reason: data.confirmation_blocked_reason });
  }
  if (data && !data.can_send_invoice_copy && data.invoice_copy_blocked_reason) {
    blockedReasons.push({ kind: 'invoice_copy', reason: data.invoice_copy_blocked_reason });
  }

  return (
    <>
    <details data-testid="order-notifications-panel" data-section="order_notifications">
      {/* The recorder names the open/close from <details>.open, so the toggle is
          one row with outcome open|close (kanban 6095f425). */}
      <summary
        className={styles.summaryLine}
        data-testid="order-notifications-summary"
        data-action="order_notifications_panel_toggle"
        data-target-order-id={orderId}
      >
        <span>
          {loadError
            ? loadError
            : summaryLine(events, contactsCount, salesSiteSends)}
        </span>
        {canManage && data && (
          <span className={styles.panelActions}>
            {/* A disabled <button> swallows mouse events in some browsers, so the
                blocked reason also sits on a wrapper that keeps the hover working
                (help cursor) — owner ask, kanban 7479fb71. */}
            <span
              className={styles.sendWrap}
              title={data.can_send_confirmation ? undefined : (data.confirmation_blocked_reason ?? undefined)}
              data-testid="send-confirmation-wrap"
            >
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={!data.can_send_confirmation || sending !== null}
                title={
                  data.confirmation_blocked_reason ??
                  (data.confirmation_sales_site_email_available
                    ? "Relays Adam's own confirmation email (exact copy) to the buyer contacts"
                    : "Adam's confirmation hasn't reached orders@ yet (it goes out when the order is approved in Sage). Sending now uses the portal's copy of his template.")
                }
                onClick={(e) => void send('confirmation', e)}
                data-testid="send-confirmation-now"
                data-action={hasSent(events, 'confirmation') ? 'order_notification_resend' : 'order_notification_send'}
                data-record-value="kind"
                value="confirmation"
                data-target-order-id={orderId}
                data-outcome="request"
              >
                {sendButtonLabel('confirmation', events, sending)}
              </button>
            </span>
            <span
              className={styles.sendWrap}
              title={data.can_send_invoice_copy ? undefined : (data.invoice_copy_blocked_reason ?? undefined)}
              data-testid="send-invoice-copy-wrap"
            >
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={!data.can_send_invoice_copy || sending !== null}
                title={
                  data.invoice_copy_blocked_reason ??
                  (data.invoice_sales_site_email_available
                    ? "Relays Adam's invoice email (with the Sage PDF) to the buyer contacts"
                    : "Adam's invoice email hasn't reached orders@ yet. Sending now uses the portal's rendering (no PDF).")
                }
                onClick={(e) => void send('invoice_copy', e)}
                data-testid="send-invoice-copy-now"
                data-action={hasSent(events, 'invoice_copy') ? 'order_notification_resend' : 'order_notification_send'}
                data-record-value="kind"
                value="invoice_copy"
                data-target-order-id={orderId}
                data-outcome="request"
              >
                {sendButtonLabel('invoice_copy', events, sending)}
              </button>
            </span>
          </span>
        )}
        {canManage && blockedReasons.length > 0 && (
          <span className={styles.blockedReasons} data-testid="order-notifications-blocked">
            {blockedReasons.map(({ kind, reason }) => (
              <span key={kind} data-testid={`send-${kind === 'confirmation' ? 'confirmation' : 'invoice-copy'}-blocked-reason`}>
                <span className={styles.blockedGlyph} aria-hidden="true">ⓘ</span>
                {KIND_LABEL[kind]}: {reason}
              </span>
            ))}
          </span>
        )}
      </summary>
      {sendError && (
        <div className={styles.error} role="alert">
          {sendError}
        </div>
      )}
      {timeline.length === 0 ? (
        <div className={styles.hint} style={{ marginTop: 6 }}>
          {contactsCount === 0
            ? 'Pick a buyer under Confirmation contacts. When Adam’s sales site sends the confirmation (Sage approval) and the invoice, the portal relays those exact emails to the buyer. Addresses already on the sales-site lists are never sent twice.'
            : 'Nothing sent yet. Adam’s confirmation is relayed to the buyer as soon as it goes out (Sage approval); the invoice email (with the PDF) once invoiced.'}
        </div>
      ) : (
        <div className={styles.eventList} data-testid="order-notifications-timeline">
          {timeline.map((row) =>
            row.source === 'portal' ? (
              <div key={row.key} className={styles.eventRow} data-testid="order-notification-event" data-source="portal">
                <span className={styles.eventKind}>
                  <span className={`${styles.sourceTag} ${styles.sourcePortal}`} data-testid="order-notification-source">
                    Portal
                  </span>
                  {KIND_LABEL[row.event.kind]}
                  {row.event.invoice_no ? ` #${row.event.invoice_no}` : ''}
                </span>
                <span className={styles.eventMeta}>
                  <span className={styles.eventRecipients}>
                    {row.event.recipients.map((r) => r.name || r.email).filter(Boolean).join(', ') || '—'}
                  </span>
                  {' · '}
                  {TRIGGER_LABEL[row.event.trigger]}
                  {' · '}
                  {formatWhen(row.event.sent_at ?? row.event.created_at)}
                  {row.event.status === 'skipped' && row.event.skip_reason && (
                    <>
                      <br />
                      <span className={styles.eventReason}>{row.event.skip_reason}</span>
                    </>
                  )}
                  {row.event.status === 'failed' && row.event.error_message && (
                    <>
                      <br />
                      <span className={styles.eventError}>{row.event.error_message}</span>
                    </>
                  )}
                </span>
                <span className={`${styles.status} ${statusClass(row.event.status)}`}>{row.event.status}</span>
              </div>
            ) : (
              <div key={row.key} className={styles.eventRow} data-testid="order-notification-event" data-source="sales_site">
                <span className={styles.eventKind}>
                  <span className={`${styles.sourceTag} ${styles.sourceSalesSite}`} data-testid="order-notification-source">
                    Sales site
                  </span>
                  {SALES_SITE_KIND_LABEL[row.send.kind]}
                  {row.send.invoice_no ? ` #${row.send.invoice_no}` : ''}
                </span>
                <span className={styles.eventMeta}>
                  <span className={styles.eventRecipients} data-testid="sales-site-recipients">
                    {row.send.recipients.length > 0
                      ? row.send.recipients.join(', ')
                      : 'no customer address on the copies (the account has no sales-site email list)'}
                  </span>
                  {' · '}
                  {"sent by Adam's sales site"}
                  {row.send.sent_at ? ` · ${formatWhen(row.send.sent_at)}` : ''}
                  <br />
                  <span className={styles.evidence} data-testid="sales-site-evidence">
                    {salesSiteEvidence(row.send)}
                  </span>
                </span>
                <span className={`${styles.status} ${styles.statusSent}`}>sent</span>
              </div>
            ),
          )}
          {salesSiteSends.length > 0 && (
            <div className={styles.hint} data-testid="sales-site-bcc-caveat">
              Sales-site rows come from its copies in the orders@ mailbox, which record To and Cc only.
              Anyone the sales site Bcc’d is not shown here.
            </div>
          )}
        </div>
      )}
    </details>
    {data && drift && drift.drifted && (
      <div
        className={styles.driftBanner}
        role="status"
        data-testid="order-confirmation-drift"
        data-section="order_confirmation_drift"
      >
        <div className={styles.driftHeadline}>{driftHeadline(drift)}</div>
        <ul className={styles.driftLines}>
          {drift.lines.map((line) => (
            <li key={line.charge} data-testid={`drift-line-${line.charge}`}>
              {driftLineText(line)}
            </li>
          ))}
        </ul>
        <div className={styles.driftActions}>
          {canManage && (
            <span
              className={styles.sendWrap}
              title={
                data.can_send_confirmation_update
                  ? "Mails the sales site's Confirmation Update template with the current surcharge / freight"
                  : (data.confirmation_update_blocked_reason ?? undefined)
              }
              data-testid="send-confirmation-update-wrap"
            >
              <button
                type="button"
                className={styles.primaryButton}
                disabled={!data.can_send_confirmation_update || sending !== null}
                onClick={() => void sendUpdate()}
                data-testid="send-confirmation-update"
                data-action="order_confirmation_update_send"
                data-target-order-id={orderId}
                data-outcome="request"
              >
                {sending === 'confirmation_update' ? 'Sending…' : 'Send confirmation update'}
              </button>
            </span>
          )}
          <span className={styles.driftRecipients} data-testid="drift-recipients">
            {updateRecipients.length > 0
              ? `Goes to ${updateRecipients.join(', ')}`
              : 'Nobody to tell yet: pick a buyer under Confirmation contacts.'}
          </span>
        </div>
        {canManage && !data.can_send_confirmation_update && data.confirmation_update_blocked_reason && (
          <div data-testid="send-confirmation-update-blocked-reason">
            <span className={styles.blockedGlyph} aria-hidden="true">ⓘ</span>
            {data.confirmation_update_blocked_reason}
          </div>
        )}
        {drift.update_sent_at && (
          <div className={styles.driftPrior} data-testid="drift-prior-update">
            A confirmation update already went out {formatWhen(drift.update_sent_at)}.
            {data.can_send_confirmation_update
              ? ' This would be a different correction (the figures or the email they hold changed since); the same one is never sent twice.'
              : ''}
          </div>
        )}
      </div>
    )}
    {data && drift && !drift.drifted && drift.update_sent_at && (
      <div className={styles.driftSettled} data-testid="order-confirmation-drift-settled">
        Confirmation update sent {formatWhen(drift.update_sent_at)}; the buyer now holds the current surcharge / freight.
      </div>
    )}
    </>
  );
};

export default OrderNotificationsPanel;
